"use client"

import { useState } from "react"
import { Check, ChevronDown, ChevronRight } from "lucide-react"
import type { ShareableTool } from "@/lib/community/publish-helpers"

interface PublishToolPickerProps {
  tools: ShareableTool[]
  selectedIds: Set<string>
  onToggle: (id: string) => void
  scenarioTitle: string
}

const CATEGORY_LABEL: Record<string, string> = {
  feishu: "飞书",
  search: "搜索",
  data: "数据",
  ai: "AI",
  ppt: "PPT",
  file: "文件",
  general: "通用",
}

export function PublishToolPicker({
  tools,
  selectedIds,
  onToggle,
  scenarioTitle,
}: PublishToolPickerProps) {
  const count = selectedIds.size

  // Group tools by agent
  const grouped = tools.reduce<Record<string, { agentName: string; tools: ShareableTool[] }>>(
    (acc, tool) => {
      if (!acc[tool.agentId]) {
        acc[tool.agentId] = { agentName: tool.agentName, tools: [] }
      }
      acc[tool.agentId].tools.push(tool)
      return acc
    },
    {}
  )

  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(
    () => new Set(Object.keys(grouped))
  )

  const toggleExpand = (agentId: string) => {
    setExpandedAgents((prev) => {
      const next = new Set(prev)
      if (next.has(agentId)) next.delete(agentId)
      else next.add(agentId)
      return next
    })
  }

  return (
    <div className="space-y-2.5">
      <p className="text-caption text-foreground/70 leading-relaxed">
        {count === 0
          ? `选择你想推荐的工具 — 来自「${scenarioTitle}」`
          : `已选 ${count} 个工具，推荐给有同样需求的用户`}
      </p>

      <div className="space-y-2">
        {Object.entries(grouped).map(([agentId, group]) => {
          const isExpanded = expandedAgents.has(agentId)
          const groupSelectedCount = group.tools.filter((t) =>
            selectedIds.has(t.id)
          ).length

          return (
            <div key={agentId}>
              {/* Agent header */}
              <button
                type="button"
                onClick={() => toggleExpand(agentId)}
                className="w-full flex items-center gap-1.5 text-left py-1"
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                )}
                <span className="text-caption font-semibold text-foreground/80">
                  {group.agentName}
                </span>
                <span className="text-micro text-muted-foreground">
                  {group.tools.length} 个工具
                  {groupSelectedCount > 0 && ` · 已选 ${groupSelectedCount}`}
                </span>
              </button>

              {/* Tool list */}
              {isExpanded && (
                <div className="ml-4 space-y-1 mt-1">
                  {group.tools.map((tool) => {
                    const isSelected = selectedIds.has(tool.id)
                    return (
                      <button
                        key={tool.id}
                        type="button"
                        onClick={() => onToggle(tool.id)}
                        className={`w-full flex items-center gap-2 rounded-md px-2.5 py-1.5 text-left transition-all duration-150 ${
                          isSelected
                            ? "bg-amber-500/8 border border-amber-500/25"
                            : "bg-secondary/20 border border-transparent hover:bg-secondary/40"
                        }`}
                      >
                        <div
                          className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-colors ${
                            isSelected
                              ? "bg-amber-500 border-amber-500"
                              : "border-border/60 bg-secondary/30"
                          }`}
                        >
                          {isSelected && (
                            <Check className="h-2 w-2 text-white" />
                          )}
                        </div>
                        <span className="text-caption font-medium text-foreground truncate flex-1">
                          {tool.label}
                        </span>
                        <span className="text-nano bg-secondary/50 text-muted-foreground px-1 rounded shrink-0">
                          {CATEGORY_LABEL[tool.category] ?? tool.category}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {tools.length === 0 && (
        <p className="text-caption text-muted-foreground/50 text-center py-4">
          该场景暂无工具数据
        </p>
      )}
    </div>
  )
}
